import React, { useState, useEffect } from "react";
import {
  Table,
  Card,
  Tag,
  Avatar,
  Typography,
  Modal,
  Descriptions,
  Divider,
  Button,
  Badge,
  Tooltip,
  Space,
  Row,
  Col,
  Image,
  Tabs,
  Statistic,
} from "antd";
import {
  GiftOutlined,
  ShopOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  StarOutlined,
  MoneyCollectOutlined,
  CalendarOutlined,
  InfoCircleOutlined,
  DollarOutlined,
  AreaChartOutlined,
  UserOutlined,
} from "@ant-design/icons";
import moment from "moment";
import "moment/locale/fr";
import _ from "lodash";

// Configure moment to use French locale
moment.locale("fr");

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const RewardDetailModal = ({ visible, reward, onClose }) => {
  if (!reward) return null;

  const getStatusInfo = (status) => {
    switch (status?.toLowerCase()) {
      case "ready":
        return { color: "green", text: "Disponible" };
      case "used":
        return { color: "purple", text: "Utilisé" };
      case "expired":
        return { color: "red", text: "Expiré" };
      default:
        return { color: "default", text: status };
    }
  };

  const statusInfo = getStatusInfo(reward.status);
  const isConditioned = reward.minPurchase;
  const usagePeriod = reward.usageDelay
    ? `Après ${reward.usageDelay} jours`
    : "Immédiate";

  const totalDistributed = reward.distributionCount * reward.value;
  const totalCost = reward.distributionCount * reward.cout;

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center" }}>
          <GiftOutlined style={{ marginRight: 8, color: "#1890ff" }} />
          <span>Détails de la Récompense</span>
        </div>
      }
      visible={visible}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          Fermer
        </Button>,
      ]}
      width={800}
    >
      <Title level={4} style={{ margin: "0 0 16px 0" }}>
        {reward.title}
      </Title>

      <Divider orientation="left">DÉTAIL RÉCOMPENSE</Divider>
      <Descriptions bordered column={2}>
        <Descriptions.Item label="Conditionné">
          {isConditioned}
        </Descriptions.Item>
        <Descriptions.Item label="Campagne">
          {reward.campaignName}
        </Descriptions.Item>
        <Descriptions.Item label="Utilisation">{usagePeriod}</Descriptions.Item>
        <Descriptions.Item label="Nature">
          <Tag>{reward.nature}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Type">
          <Tag color="blue">{reward.type}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Valeur">
          <Tag color="green" icon={<MoneyCollectOutlined />}>
            {reward.value}€
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Montant Total Distribué">
          <Text strong>{totalDistributed}€</Text>
        </Descriptions.Item>
        <Descriptions.Item label="Coût Total">
          <Text strong>{totalCost}€</Text>
        </Descriptions.Item>
        <Descriptions.Item label="Nombre Distribution">
          {reward.distributionCount}
        </Descriptions.Item>
        <Descriptions.Item label="Nombre Utilisation">
          {reward.usageCount}
        </Descriptions.Item>
        <Descriptions.Item label="Évaluation">
          <StarOutlined style={{ color: "#fadb14" }} />{" "}
          {reward?.avgRating?.toFixed(1)}
        </Descriptions.Item>
      </Descriptions>

      {/* <Divider orientation="left">Détails de l'Établissement</Divider>
      <Descriptions bordered column={2}>
        <Descriptions.Item label="Nom">
          <Text strong>{reward.etabName}</Text>
        </Descriptions.Item>
        <Descriptions.Item label="Emplacement">
          {reward.etabLocation}
        </Descriptions.Item>
        <Descriptions.Item label="Description">
          {reward.etabDescription}
        </Descriptions.Item>
        <Descriptions.Item label="Téléphone">
          <Text copyable>{reward.etabPhone}</Text>
        </Descriptions.Item>
      </Descriptions> */}
    </Modal>
  );
};

const RewardDashboard = () => {
  const [rewards, setRewards] = useState([]);
  const [groupedRewards, setGroupedRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReward, setSelectedReward] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const user = JSON.parse(localStorage.getItem("user") || '{"type": "admin"}');

  useEffect(() => {
    // Simulate API call with your provided data
    const fetchRewards = async () => {
      try {
        setLoading(true);

        // In a real application, this would be fetched from an API
        let response =
          user.type === "admin"
            ? await fetch("https://trend-me.com/rewards")
            : await fetch("https://trend-me.com/rewards/ettab/" + user?.IdEtab);
        let data = await response.json();

        // Process the data to match our component needs
        const processedRewards = data.map((reward) => ({
          _id: reward._id,
          title: reward.title,
          campaignName: reward.postId.selectedCampaignName,
          nature: reward.nature,
          type: reward.type,
          value: reward.value,
          cout: reward.cout,
          status: reward.status,
          dateCreation: new Date(reward.dateCreation),
          dateUse: reward.dateUse ? new Date(reward.dateUse) : null,
          dateExp: reward.dateExp ? new Date(reward.dateExp) : null,
          rating: reward.rating || 0,
          trendPrize: reward.trendPrize,
          etabName: reward.postId.etablissement.name,
          etabLogo: `https://trend-me.com/upload/${reward.postId.etablissement.avatar}`,
          etabLocation: reward.postId.etablissement.location,
          etabDescription: reward.postId.etablissement.description,
          etabPhone: reward.postId.etablissement.phone,
          usageDelay: reward.usageDelay || 0,
          minPurchase: reward.minPurchase || 0,
        }));

        setRewards(processedRewards);

        // Group rewards by nature
        const grouped = _.chain(processedRewards)
          .groupBy("nature")
          .map((items, nature) => {
            // const distributionCount = items.filter(
            //   (i) => i.status?.toLowerCase() === "ready"
            // ).length;
            const distributionCount = items.length;
            const usageCount = items.filter(
              (i) => i.status?.toLowerCase() === "used"
            ).length;

            const avgRating =
              items.reduce((sum, item) => sum + (item.rating || 0), 0) /
              (items.length || 1);

            return {
              nature,
              title: items[0].title,
              campaignName: items[0].campaignName,
              type: items[0].type,
              value: items[0].value,
              cout: items[0].cout,
              distributionCount,
              usageCount,
              avgRating,
              etabName: items[0].etabName,
              etabLogo: items[0].etabLogo,
              etabLocation: items[0].etabLocation,
              etabDescription: items[0].etabDescription,
              etabPhone: items[0].etabPhone,
              usageDelay: items[0].usageDelay || 0,
              minPurchase: items[0].minPurchase || 0,
              items,
            };
          })
          .value();

        setGroupedRewards(grouped);
      } catch (error) {
        console.error("Erreur lors de la récupération des récompenses:", error);
        // Fall back to empty arrays if fetch fails
        setRewards([]);
        setGroupedRewards([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRewards();
  }, []);

  const columns = [
    {
      title: "Nom récompense",
      dataIndex: "title",
      key: "title",
      render: (text, record) => (
        <div style={{ display: "flex", alignItems: "center" }}>
          <Avatar
            size="small"
            src={record.etabLogo}
            icon={<ShopOutlined />}
            style={{ marginRight: 8 }}
          />
          <Text strong>{text}</Text>
        </div>
      ),
    },
    {
      title: "Campagne",
      dataIndex: "campaignName",
      key: "campaignName",
      render: (text) => <Tag color="processing">{text}</Tag>,
    },
    {
      title: "Nature",
      dataIndex: "nature",
      key: "nature",
      render: (text) => <Tag>{text}</Tag>,
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (text) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: "Valeur",
      dataIndex: "value",
      key: "value",
      render: (text) => (
        <Tag color="green" icon={<MoneyCollectOutlined />}>
          {text}€
        </Tag>
      ),
    },
    {
      title: "Coût",
      dataIndex: "cout",
      key: "cout",
      render: (text) => (
        <Tag color="orange" icon={<DollarOutlined />}>
          {text}€
        </Tag>
      ),
    },
    {
      title: "Nombre Distribution",
      dataIndex: "distributionCount",
      key: "distributionCount",
      render: (text) => text,
    },
    {
      title: "Nombre Utilisation",
      dataIndex: "usageCount",
      key: "usageCount",
      render: (text) => text,
    },
    {
      title: "Évaluation",
      dataIndex: "avgRating",
      key: "avgRating",
      render: (text) => (
        <Space>
          <StarOutlined style={{ color: "#fadb14" }} />
          {text.toFixed(1)}
        </Space>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => {
            setSelectedReward(record);
            setModalVisible(true);
          }}
        >
          Détails
        </Button>
      ),
    },
  ];

  const renderSummary = () => {
    const totalDistributions = groupedRewards.reduce(
      (sum, item) => sum + item.distributionCount,
      0
    );
    const totalUsages = groupedRewards.reduce(
      (sum, item) => sum + item.usageCount,
      0
    );
    const totalValue = groupedRewards.reduce(
      (sum, item) => sum + item.distributionCount * item.value,
      0
    );
    const totalCost = groupedRewards.reduce(
      (sum, item) => sum + item.distributionCount * item.cout,
      0
    );

    return (
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16}>
          <Col span={6}>
            <Statistic
              title="Total Distributions"
              value={totalDistributions}
              prefix={<GiftOutlined />}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Total Récompense Utilisé"
              value={totalUsages}
              prefix={<GiftOutlined />}
              valueStyle={{ color: "#52c41a" }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Valeur Totale Distribuée"
              value={totalValue}
              prefix="€"
              valueStyle={{ color: "#1890ff" }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Coût Total"
              value={totalCost}
              prefix="€"
              valueStyle={{ color: "#fa8c16" }}
            />
          </Col>
        </Row>
      </Card>
    );
  };

  return (
    <div style={{ padding: 24 }}>
      <Title level={3}>Tableau de Bord des Récompenses</Title>
      {!loading && renderSummary()}

      <Card loading={loading}>
        <Tabs defaultActiveKey="summary">
          <TabPane
            tab={
              <span>
                <AreaChartOutlined />
                Groupé par Nature
              </span>
            }
            key="summary"
          >
            <Table
              columns={columns}
              dataSource={groupedRewards}
              rowKey="nature"
              pagination={{ pageSize: 10 }}
              locale={{
                emptyText: "Aucune récompense trouvée",
                triggerDesc: "Cliquer pour trier en ordre décroissant",
                triggerAsc: "Cliquer pour trier en ordre croissant",
                cancelSort: "Cliquer pour annuler le tri",
              }}
            />
          </TabPane>
        </Tabs>
      </Card>

      <RewardDetailModal
        visible={modalVisible}
        reward={selectedReward}
        onClose={() => setModalVisible(false)}
      />
    </div>
  );
};

export default RewardDashboard;
